import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { search, contacts, offices, contactRoles } from "@/api/addressBook.service";
import type { SearchCompanyResult, Contact, Office } from "@/api/addressBook.service";
import styles from "./AddTargetModal.module.css";

type TargetType = "organisation" | "lawyer";

type AddTargetResult = {
  id: string;
  name: string;
  subtitle: string;
  companyId: string;
  contactId?: string;
  officeId?: string;
  officeName?: string;
  /** The submission table this row is created against. Undefined for an untabled row — the
   * only option when the practice area has zero or one submission table of this grain. */
  rankingTableId?: string;
};

/** A ranking table the researcher can pick when adding — a genuine submission target only:
 * `submissionType === "firm"` for organisations, `"lawyer"` for lawyers. */
type RankingTableOption = { id: string; name: string };

type AddTargetModalProps = {
  type: TargetType;
  open: boolean;
  onClose: () => void;
  onAdd: (target: AddTargetResult) => void;
  adding?: boolean;
  /** Surfaced on the confirm step so a failed add is visible without losing the chosen table. */
  error?: string | null;
  /** Which submission tables each entity already has a row for, keyed by companyId (org) or
   * contactId (lawyer). Absent means no existing row; an empty set means an untabled row only. */
  existingTableIdsByEntityId?: Map<string, Set<string>>;
  /** Submission tables to offer. Empty means nothing can be added at all; one means the add is
   * untabled and the table step is skipped; two or more shows the step. */
  rankingTables?: RankingTableOption[];
};

function SearchIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function ExternalLinkIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function BackIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function BuildingIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
      <path d="M9 22v-4h6v4" />
      <path d="M8 6h.01" />
      <path d="M16 6h.01" />
      <path d="M8 10h.01" />
      <path d="M16 10h.01" />
      <path d="M8 14h.01" />
      <path d="M16 14h.01" />
    </svg>
  );
}

function Spinner() {
  return (
    <svg
      className={styles.spinner}
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.2" />
      <path
        d="M12 2a10 10 0 0 1 10 10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function mapCompany(c: SearchCompanyResult): AddTargetResult {
  const count = c.offices?.length ?? 0;
  const subtitle = count > 0 ? `${count} office${count !== 1 ? "s" : ""}` : "";
  return {
    id: c.id,
    name: c.name,
    subtitle,
    companyId: c.id,
  };
}

function mapContact(c: Contact): AddTargetResult {
  const subtitle = [c.jobTitle, c.id].filter(Boolean).join(" · ");
  return {
    id: c.id,
    name: c.name,
    subtitle,
    companyId: c.companyId ?? "",
    contactId: c.id,
  };
}

export function AddTargetModal({
  type,
  open,
  onClose,
  onAdd,
  adding,
  error,
  existingTableIdsByEntityId,
  rankingTables,
}: AddTargetModalProps) {
  const [query, setQuery] = useState("");
  const [selectedTableId, setSelectedTableId] = useState("");
  // Set once a result is chosen from a practice area with more than one submission table — the
  // modal's second step, confirming which table it's created against. Null is the search step.
  const [pendingTarget, setPendingTarget] = useState<AddTargetResult | null>(null);
  const [results, setResults] = useState<AddTargetResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [activeIndex, setActiveIndex] = useState(-1);

  const [expandedCompanyId, setExpandedCompanyId] = useState<string | null>(null);
  const [expandedOffices, setExpandedOffices] = useState<Office[]>([]);
  const [loadingOffices, setLoadingOffices] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const label = type === "organisation" ? "organisation" : "lawyer";
  const placeholder =
    type === "organisation" ? "Search firms by name..." : "Search lawyers by name...";

  const submissionTables = useMemo(() => rankingTables ?? [], [rankingTables]);
  const hasSubmissionTables = rankingTables === undefined || submissionTables.length > 0;
  // One table leaves nothing to choose: Add creates the row immediately, with no second step.
  const showRankingTableStep = submissionTables.length > 1;
  const canConfirm = selectedTableId !== "";

  // The submission tables a given entity does NOT already have a row for — what step 2 offers.
  // Recomputed per pendingTarget, since two different results in the same session can occupy
  // different tables.
  const occupiedTablesOf = useCallback(
    (result: AddTargetResult): Set<string> =>
      existingTableIdsByEntityId?.get(result.contactId ?? result.companyId) ?? new Set<string>(),
    [existingTableIdsByEntityId],
  );

  // Unavailable only once the entity occupies EVERY submission table of this grain: a row in one
  // of three leaves two open. With no table at all the empty state speaks, never this label.
  const isAlreadyAdded = useCallback(
    (result: AddTargetResult): boolean => {
      if (!hasSubmissionTables) return false;
      const idToCheck = result.contactId ?? result.companyId;
      if (!existingTableIdsByEntityId?.has(idToCheck)) return false;
      // A lone table means the add is untabled, so any existing row already fills the one slot.
      if (!showRankingTableStep) return true;
      const occupied = occupiedTablesOf(result);
      return submissionTables.every(t => occupied.has(t.id));
    },
    [
      existingTableIdsByEntityId,
      hasSubmissionTables,
      showRankingTableStep,
      submissionTables,
      occupiedTablesOf,
    ],
  );

  const pendingOccupiedTables = pendingTarget ? occupiedTablesOf(pendingTarget) : new Set<string>();

  useEffect(() => {
    if (open) {
      setQuery("");
      setResults([]);
      setHasMore(false);
      setTotal(0);
      setOffset(0);
      setActiveIndex(-1);
      setExpandedCompanyId(null);
      setExpandedOffices([]);
      setLoadingOffices(false);
      setPendingTarget(null);
      setSelectedTableId("");
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // The shared "commit" point for every add trigger (an office row, its Add button, or the
  // office-less lawyer shortcut): advance to the confirm step when there's a table to choose,
  // otherwise create the untabled row immediately exactly as before this feature existed.
  const proceed = useCallback(
    (result: AddTargetResult) => {
      if (showRankingTableStep) {
        setSelectedTableId("");
        setPendingTarget(result);
      } else if (submissionTables.length === 1) {
        onAdd({ ...result, rankingTableId: submissionTables[0].id });
      } else {
        onAdd(result);
      }
    },
    [showRankingTableStep, submissionTables, onAdd],
  );

  const handleBack = useCallback(() => setPendingTarget(null), []);

  const handleConfirm = useCallback(() => {
    if (!pendingTarget || adding || !canConfirm) return;
    onAdd({ ...pendingTarget, rankingTableId: selectedTableId || undefined });
  }, [pendingTarget, adding, canConfirm, onAdd, selectedTableId]);

  useEffect(() => {
    setExpandedCompanyId(null);
    setExpandedOffices([]);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!query.trim()) {
      setResults([]);
      setHasMore(false);
      setTotal(0);
      setOffset(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    let cancelled = false;
    debounceRef.current = setTimeout(async () => {
      try {
        if (type === "organisation") {
          const res = await search.companies(query);
          if (cancelled) return;
          setResults(res.companies.map(mapCompany));
          setHasMore(res.pagination.hasMore ?? false);
          setTotal(res.pagination.total);
          setOffset(res.pagination.limit);
        } else {
          const res = await contacts.getAll({ search: query, limit: 100, offset: 0 });
          if (cancelled) return;
          setResults(res.data.map(mapContact));
          setHasMore(res.pagination.hasMore ?? false);
          setTotal(res.pagination.total);
          setOffset(100);
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Search failed:", err);
        setResults([]);
        setHasMore(false);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setActiveIndex(-1);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, type, open]);

  useEffect(() => {
    if (!open) return;
    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open, onClose]);

  const handleLoadMore = useCallback(async () => {
    if (loadingMore || !hasMore || !query.trim()) return;
    setLoadingMore(true);
    try {
      if (type === "organisation") {
        const res = await search.companies(query, 100, offset);
        setResults(prev => [...prev, ...res.companies.map(mapCompany)]);
        setHasMore(res.pagination.hasMore ?? false);
        setTotal(res.pagination.total);
        setOffset(prev => prev + res.pagination.limit);
      } else {
        const res = await contacts.getAll({ search: query, limit: 100, offset });
        setResults(prev => [...prev, ...res.data.map(mapContact)]);
        setHasMore(res.pagination.hasMore ?? false);
        setTotal(res.pagination.total);
        setOffset(prev => prev + 100);
      }
    } catch (err) {
      console.error("Load more failed:", err);
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [query, type, offset, loadingMore, hasMore]);

  const handleCompanyClick = useCallback(
    async (result: AddTargetResult) => {
      if (expandedCompanyId === result.companyId) {
        setExpandedCompanyId(null);
        setExpandedOffices([]);
        return;
      }
      setExpandedCompanyId(result.companyId);
      setExpandedOffices([]);
      setLoadingOffices(true);
      try {
        const res = await offices.getAll({ companyId: result.companyId, limit: 100 });
        setExpandedOffices(res.data);
      } catch (err) {
        console.error("Fetch offices failed:", err);
        setExpandedOffices([]);
      } finally {
        setLoadingOffices(false);
      }
    },
    [expandedCompanyId],
  );

  const handleLawyerClick = useCallback(
    async (result: AddTargetResult) => {
      if (!result.contactId) {
        proceed(result);
        return;
      }
      if (expandedCompanyId === result.contactId) {
        setExpandedCompanyId(null);
        setExpandedOffices([]);
        return;
      }
      setExpandedCompanyId(result.contactId);
      setExpandedOffices([]);
      setLoadingOffices(true);
      try {
        const rolesRes = await contactRoles.getAll({ contactId: result.contactId, limit: 100 });
        const uniqueOfficeIds = [...new Set(rolesRes.data.map(r => r.officeId))];
        const officeResults = await Promise.all(
          uniqueOfficeIds.map(id => offices.getById(id).catch(() => null)),
        );
        const fetched = officeResults
          .filter((r): r is NonNullable<typeof r> => r !== null)
          .map(r => r.data);
        setExpandedOffices(fetched);
      } catch (err) {
        console.error("Fetch contact roles failed:", err);
        setExpandedOffices([]);
      } finally {
        setLoadingOffices(false);
      }
    },
    [expandedCompanyId, proceed],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!results.length) return;
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setActiveIndex(prev => (prev < results.length - 1 ? prev + 1 : 0));
          break;
        case "ArrowUp":
          e.preventDefault();
          setActiveIndex(prev => (prev > 0 ? prev - 1 : results.length - 1));
          break;
        case "Enter": {
          e.preventDefault();
          if (activeIndex >= 0 && activeIndex < results.length) {
            const target = results[activeIndex];
            if (!isAlreadyAdded(target) && !adding) {
              if (type === "organisation") {
                handleCompanyClick(target);
              } else {
                handleLawyerClick(target);
              }
            }
          }
          break;
        }
      }
    },
    [
      results,
      activeIndex,
      onAdd,
      isAlreadyAdded,
      type,
      handleCompanyClick,
      handleLawyerClick,
      adding,
    ],
  );

  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const item = listRef.current.children[activeIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  useEffect(() => {
    const el = listRef.current;
    if (!el || !hasMore) return;

    function onScroll() {
      if (!el) return;
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
      if (nearBottom && !loadingMore) {
        handleLoadMore();
      }
    }

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [hasMore, loadingMore, handleLoadMore]);

  if (!open) return null;

  return (
    <div className={styles.overlay} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal} role="dialog" aria-modal="true" aria-label={`Add ${label}`}>
        <div className={styles.header}>
          {pendingTarget && (
            <button
              type="button"
              className={styles.backButton}
              onClick={handleBack}
              aria-label="Back to results"
            >
              <BackIcon />
            </button>
          )}
          <h2 className={styles.title}>
            {pendingTarget ? `Add ${pendingTarget.name}` : `Add ${label}`}
          </h2>
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        {!hasSubmissionTables ? (
          <div className={styles.results}>
            <div className={styles.emptyState}>
              <span>This practice area has no {label} ranking table that accepts submissions.</span>
              <span className={styles.emptyHint}>
                Nobody can be added here until one is configured.
              </span>
            </div>
          </div>
        ) : pendingTarget ? (
          <div className={styles.confirmStep}>
            <div className={styles.confirmEntity}>
              <span className={styles.resultName}>{pendingTarget.name}</span>
              {pendingTarget.officeName && (
                <span className={styles.resultSubtitle}>{pendingTarget.officeName}</span>
              )}
            </div>
            <div className={styles.rankingTableField}>
              <label className={styles.rankingTableLabel} htmlFor="add-target-ranking-table">
                Ranking table <span aria-hidden="true">*</span>
              </label>
              <select
                id="add-target-ranking-table"
                className={styles.rankingTableSelect}
                value={selectedTableId}
                onChange={e => setSelectedTableId(e.target.value)}
                required
                aria-required="true"
                autoFocus
              >
                <option value="" disabled>
                  Select a table…
                </option>
                {/* An occupied table stays visible but unselectable, so the researcher can see
                    the table exists and that this entity is already in it. */}
                {submissionTables.map(table => (
                  <option
                    key={table.id}
                    value={table.id}
                    disabled={pendingOccupiedTables.has(table.id)}
                  >
                    {pendingOccupiedTables.has(table.id)
                      ? `${table.name} (already added)`
                      : table.name}
                  </option>
                ))}
              </select>
              <span className={styles.rankingTableHelp}>
                Which table {pendingTarget.name} is submitted for
              </span>
            </div>
            {error && (
              <div className={styles.confirmError} role="alert">
                {error}
              </div>
            )}
            <div className={styles.confirmFooter}>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={handleBack}
                disabled={adding}
              >
                Back
              </button>
              <button
                type="button"
                className={styles.primaryButton}
                onClick={handleConfirm}
                disabled={adding || !canConfirm}
              >
                {adding ? "Adding..." : `Add ${label}`}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className={styles.searchWrap}>
              <span className={styles.searchIcon}>
                <SearchIcon />
              </span>
              <input
                ref={inputRef}
                type="text"
                className={styles.searchInput}
                placeholder={placeholder}
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                aria-label={`Search ${label}s`}
              />
            </div>

            <div className={styles.results} ref={listRef}>
              {loading ? (
                <div className={styles.emptyState}>
                  <Spinner />
                  Searching...
                </div>
              ) : !query.trim() ? (
                <div className={styles.emptyState}>
                  <span>Start typing to search the Address Book</span>
                  <span className={styles.emptyHint}>Results will appear as you type</span>
                </div>
              ) : results.length === 0 ? (
                <div className={styles.emptyState}>
                  <span>
                    No {label}s found for &ldquo;{query}&rdquo;
                  </span>
                  <span className={styles.emptyHint}>Try a different search term</span>
                </div>
              ) : (
                results.map((result, index) => {
                  const alreadyAdded = isAlreadyAdded(result);
                  const isExpanded =
                    type === "organisation"
                      ? expandedCompanyId === result.companyId
                      : expandedCompanyId === result.contactId;
                  return (
                    <Fragment key={result.id}>
                      <div
                        className={`${styles.resultItem} ${index === activeIndex ? styles.resultItemActive : ""} ${alreadyAdded ? styles.resultItemDisabled : ""} ${isExpanded ? styles.resultItemExpanded : ""}`}
                        onMouseEnter={() => setActiveIndex(index)}
                        onClick={() => {
                          if (alreadyAdded || adding) return;
                          if (type === "organisation") {
                            handleCompanyClick(result);
                          } else {
                            handleLawyerClick(result);
                          }
                        }}
                      >
                        <div className={styles.resultInfo}>
                          <span className={styles.resultName}>{result.name}</span>
                          <span className={styles.resultSubtitle}>
                            {isExpanded && expandedOffices.length > 0
                              ? `${expandedOffices.length} office${expandedOffices.length !== 1 ? "s" : ""}`
                              : result.subtitle || ""}
                          </span>
                          {alreadyAdded && (
                            <span className={styles.alreadyAddedLabel}>Already added</span>
                          )}
                        </div>
                        <div className={styles.resultActions}>
                          <a
                            href={
                              result.contactId
                                ? `/address-book/contacts/${result.contactId}`
                                : `/address-book/companies/${result.companyId}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.viewLink}
                            onClick={e => e.stopPropagation()}
                            title="View in Address Book"
                          >
                            <span className={styles.viewLinkText}>View</span>
                            <ExternalLinkIcon />
                          </a>
                          {!alreadyAdded && (
                            <span
                              className={`${styles.chevron} ${isExpanded ? styles.chevronExpanded : ""}`}
                            >
                              <ChevronIcon />
                            </span>
                          )}
                        </div>
                      </div>
                      {isExpanded &&
                        (loadingOffices ? (
                          <div className={styles.officeLoading}>
                            <Spinner />
                            <span>Loading offices...</span>
                          </div>
                        ) : (
                          expandedOffices.map(office => {
                            const location = [office.city, office.countryName]
                              .filter(Boolean)
                              .join(", ");
                            return (
                              <div
                                key={office.id}
                                className={`${styles.officeRow} ${adding ? styles.resultItemDisabled : ""}`}
                                onClick={() => {
                                  if (adding) return;
                                  proceed({
                                    ...result,
                                    officeId: office.id,
                                    officeName:
                                      [office.city, office.countryName]
                                        .filter(Boolean)
                                        .join(", ") || office.name,
                                  });
                                }}
                              >
                                <span className={styles.officeIcon}>
                                  <BuildingIcon />
                                </span>
                                <div className={styles.officeInfo}>
                                  <span className={styles.officeRowName}>{office.name}</span>
                                  {location && (
                                    <span className={styles.officeRowLocation}>{location}</span>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  className={styles.addButton}
                                  onClick={e => {
                                    e.stopPropagation();
                                    if (adding) return;
                                    proceed({
                                      ...result,
                                      officeId: office.id,
                                      officeName:
                                        [office.city, office.countryName]
                                          .filter(Boolean)
                                          .join(", ") || office.name,
                                    });
                                  }}
                                  disabled={adding}
                                >
                                  {adding ? "Adding..." : "Add"}
                                </button>
                              </div>
                            );
                          })
                        ))}
                    </Fragment>
                  );
                })
              )}
            </div>

            <div className={styles.footer}>
              {loadingMore && <span>Loading more...</span>}
              {!loadingMore && results.length > 0 && (
                <span>
                  Showing {results.length}
                  {total > 0 ? ` of ${total}` : ""} result{results.length !== 1 ? "s" : ""}
                </span>
              )}
              {results.length === 0 && !loading && (
                <span>Search results from the Address Book</span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export type { TargetType, AddTargetResult, AddTargetModalProps };
